import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle, Banknote, FileWarning, TrendingUp } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardBody, CardHeader, StatTile } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { SectionSpinner } from '@/components/ui/Spinner';
import { EmptyState, TBody, TD, TH, THead, TR, Table } from '@/components/ui/Table';
import { dashboardApi } from '@/services/endpoints/transactions';
import { queryKeys } from '@/services/queryKeys';
import { formatCents, formatCompact, formatMoney } from '@/domain/money';
import { addDays, formatAxisDate, formatDate, today } from '@/lib/dateUtils';

/**
 * Every widget the requirements list, filled by a single API call.
 *
 * Chart series come from the server already zero-filled and, for the per-agent
 * chart, already pivoted — so a quiet day draws a point at zero instead of the
 * line jumping across a gap, and this component does no data reshaping.
 */
export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();

  const [range, setRange] = useState<{ from: string; to: string }>({
    from: addDays(today(), -29),
    to: today(),
  });

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.dashboard.main(range.from, range.to),
    queryFn: () => dashboardApi.main(range.from, range.to),
  });

  if (isLoading) return <SectionSpinner />;
  if (!data) return <EmptyState title={t('errors.unknown')} />;

  const { totals, dailyVariation, agentVariation, topCustomersByPending, topCustomersByInvoiced, overdue, routes } =
    data;

  // Recharts works in numbers, so cents are converted to rupees once here
  // rather than in every axis and tooltip formatter.
  const dailySeries = dailyVariation.map((day) => ({
    date: formatAxisDate(day.date),
    total: day.totalCents / 100,
    cash: day.cashCents / 100,
    cheque: day.chequeCents / 100,
  }));

  const agentSeries = agentVariation.dates.map((date, index) => {
    const point: Record<string, string | number> = { date: formatAxisDate(date) };
    for (const series of agentVariation.series) {
      point[series.agentName] = (series.points[index] ?? 0) / 100;
    }
    return point;
  });

  const AGENT_COLOURS = ['#1B8A3E', '#2563eb', '#d97706', '#7c3aed', '#dc2626', '#0891b2'];

  const overdueSeries = overdue.buckets.map((bucket) => ({
    bucket: bucket.bucket,
    amount: bucket.outstandingCents / 100,
    count: bucket.invoiceCount,
  }));

  const RANGES = [
    { label: t('dashboard.last7Days'), days: 7 },
    { label: t('dashboard.last30Days'), days: 30 },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('dashboard.title')}
        actions={
          <div className="flex gap-2">
            {RANGES.map((option) => (
              <Button
                key={option.days}
                variant="outline"
                size="sm"
                onClick={() => setRange({ from: addDays(today(), -(option.days - 1)), to: today() })}
              >
                {option.label}
              </Button>
            ))}
          </div>
        }
      />

      {/* Headline figures: issued vs collected vs owed. */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label={t('dashboard.totalInvoiced')}
          value={formatMoney(totals.totalInvoicedCents)}
          icon={<TrendingUp className="h-5 w-5" />}
        />
        <StatTile
          label={t('dashboard.totalCollected')}
          value={formatMoney(totals.totalCollectedCents)}
          sublabel={`${totals.collectionRatePercent}% ${t('dashboard.collectionRate')}`}
          tone="positive"
          icon={<Banknote className="h-5 w-5" />}
        />
        <StatTile
          label={t('dashboard.totalOutstanding')}
          value={formatMoney(totals.totalOutstandingCents)}
          sublabel={`${totals.pendingInvoiceCount} ${t('dashboard.pendingInvoices')}`}
          tone="warning"
          icon={<FileWarning className="h-5 w-5" />}
        />
        <StatTile
          label={t('dashboard.overdueAmount')}
          value={formatMoney(totals.overdueAmountCents)}
          sublabel={`${totals.overdueInvoiceCount} ${t('dashboard.overdueInvoices')}`}
          tone="danger"
          icon={<AlertTriangle className="h-5 w-5" />}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader title={t('dashboard.dailyCollection')} />
          <CardBody>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={dailySeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(value: number) => formatCompact(value * 100)} />
                <Tooltip
                  formatter={(value: number) => formatCents(Math.round(value * 100))}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="total" name={t('common.total')} stroke="#1B8A3E" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="cash" name={t('collection.cash')} stroke="#2563eb" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="cheque" name={t('collection.cheque')} stroke="#d97706" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={t('dashboard.agentCollection')} />
          <CardBody>
            {agentVariation.series.length === 0 ? (
              <EmptyState title={t('dashboard.noCollections')} />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={agentSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(value: number) => formatCompact(value * 100)} />
                  <Tooltip
                    formatter={(value: number) => formatCents(Math.round(value * 100))}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {agentVariation.series.map((series, index) => (
                    <Line
                      key={series.agentId}
                      type="monotone"
                      dataKey={series.agentName}
                      stroke={AGENT_COLOURS[index % AGENT_COLOURS.length]}
                      strokeWidth={1.8}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader title={t('dashboard.topByPending')} />
          <Table>
            <THead>
              <TR>
                <TH>{t('shop.clientName')}</TH>
                <TH>{t('shop.agent')}</TH>
                <TH align="center">{t('shop.openInvoices')}</TH>
                <TH align="right">{t('shop.outstanding')}</TH>
              </TR>
            </THead>
            <TBody>
              {topCustomersByPending.map((customer) => (
                <TR key={customer.shopId}>
                  <TD className="font-medium text-slate-900">{customer.shopName}</TD>
                  <TD className="text-slate-500">{customer.agentName ?? '—'}</TD>
                  <TD align="center" numeric>
                    {customer.invoiceCount}
                  </TD>
                  <TD align="right" numeric className="font-semibold">
                    {formatCents(customer.outstandingCents)}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>

        <Card>
          <CardHeader title={t('dashboard.topByInvoiced')} />
          <Table>
            <THead>
              <TR>
                <TH>{t('shop.clientName')}</TH>
                <TH>{t('shop.route')}</TH>
                <TH align="center">{t('invoice.title')}</TH>
                <TH align="right">{t('dashboard.totalInvoiced')}</TH>
              </TR>
            </THead>
            <TBody>
              {topCustomersByInvoiced.map((customer) => (
                <TR key={customer.shopId}>
                  <TD className="font-medium text-slate-900">{customer.shopName}</TD>
                  <TD className="text-slate-500">{customer.routeName ?? '—'}</TD>
                  <TD align="center" numeric>
                    {customer.invoiceCount}
                  </TD>
                  <TD align="right" numeric className="font-semibold">
                    {formatCents(customer.invoicedCents)}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader title={t('dashboard.overdueAgeing')} />
          <CardBody>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={overdueSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(value: number) => formatCompact(value * 100)} />
                <Tooltip
                  formatter={(value: number) => formatCents(Math.round(value * 100))}
                  contentStyle={{ fontSize: 12 }}
                />
                <Bar dataKey="amount" name={t('dashboard.overdueAmount')} radius={[4, 4, 0, 0]}>
                  {/* Older debt is redder — the bucket is the whole point. */}
                  {overdueSeries.map((entry, index) => (
                    <Cell
                      key={entry.bucket}
                      fill={['#fbbf24', '#fb923c', '#f87171', '#dc2626'][index] ?? '#dc2626'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={t('dashboard.routeView')} />
          <Table>
            <THead>
              <TR>
                <TH>{t('route.one')}</TH>
                <TH>{t('shop.agent')}</TH>
                <TH align="center">{t('dashboard.shops')}</TH>
                <TH align="right">{t('dashboard.collectedToday')}</TH>
                <TH align="right">{t('shop.outstanding')}</TH>
              </TR>
            </THead>
            <TBody>
              {routes.map((route) => (
                <TR key={route.routeId}>
                  <TD className="font-medium text-slate-900">{route.routeName}</TD>
                  <TD className="text-slate-500">
                    {route.agentName ?? <Badge tone="warning">{t('route.noAgent')}</Badge>}
                  </TD>
                  <TD align="center" numeric>
                    {route.shopCount}
                  </TD>
                  <TD align="right" numeric>
                    {route.collectedTodayCents > 0 ? formatCents(route.collectedTodayCents) : '—'}
                  </TD>
                  <TD align="right" numeric>
                    {formatCents(route.outstandingCents)}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      </div>

      <Card>
        <CardHeader title={t('dashboard.worstOverdue')} />
        <Table>
          <THead>
            <TR>
              <TH>{t('invoice.invoiceNo')}</TH>
              <TH>{t('invoice.customer')}</TH>
              <TH>{t('shop.agent')}</TH>
              <TH>{t('invoice.dueDate')}</TH>
              <TH align="center">{t('dashboard.bucket')}</TH>
              <TH align="right">{t('invoice.outstanding')}</TH>
            </TR>
          </THead>
          <TBody>
            {overdue.worst.map((invoice) => (
              <TR key={invoice.invoiceId}>
                <TD className="font-medium text-slate-900">{invoice.invoiceNo}</TD>
                <TD>{invoice.shopName}</TD>
                <TD className="text-slate-500">{invoice.agentName ?? '—'}</TD>
                <TD>{formatDate(invoice.dueDate)}</TD>
                <TD align="center">
                  <Badge tone={invoice.daysOverdue > 90 ? 'danger' : 'warning'}>
                    {invoice.daysOverdue}
                  </Badge>
                </TD>
                <TD align="right" numeric className="font-semibold">
                  {formatCents(invoice.outstandingCents)}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Card>
    </div>
  );
}
